import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchBranchProducts } from "../services/productService";
import { param, useListUrlState, yesNo } from "./useUrlState";

const FILTERS = {
  search: param.string("", { debounce: 400 }),
  category: param.string(),
  brand: param.string(),
  isActive: yesNo(),
  isAvailableOnline: yesNo(),
  inStock: yesNo(),
  lowStock: param.flag(),
};

/**
 * One branch's products, with the list's paging and filter state.
 * `reload()` refetches the current page after a mutation.
 */
export default function useProducts(branchId) {
  const { t, i18n } = useTranslation();

  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  // Page, filters and search live in the URL — see useUrlState.
  const list = useListUrlState(FILTERS);
  const { page, filters } = list;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { products: list, meta: pagination } = await fetchBranchProducts(branchId, {
        page,
        filters,
      });
      setProducts(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("products.loadFailed")));
      setProducts([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, page, filters, i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    products,
    meta,
    page,
    setPage: list.setPage,
    filters,
    applyFilters: list.applyFilters,
    resetFilters: list.resetFilters,
    searchTerm: list.searchTerm,
    setSearchTerm: list.setSearchTerm,
    submitSearch: list.submitSearch,
    loading,
    error,
    reload: load,
  };
}
